/* eslint-disable react-refresh/only-export-components */
import {
  useContext,
  createContext,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { clearUserState } from "./auth";
import { checkSession } from "./session";
import { decodeJWT } from "@/utils/encoders/encoders";
import { LOGIN_ROUTE, SHARING_ABSTRACT_ROUTE } from "@/routing/app-routes";
import { usePostHog } from "posthog-js/react";
import {
  posthogIdentifyUser,
  posthogIdentifyWorkspace,
  posthogReset,
} from "@/utils/posthog-utils/posthog";
import useGetAuthUser from "@/api/routes/users/get-auth-user";
import { PROVIDERS } from "@/components/pages/login-page/login-constants";
import { getStoredToken, removeToken, setToken } from "./token-utils";
import {
  getLastWorkspaceId,
  resolveWorkspaceId,
  setLastWorkspaceId,
} from "@/utils/workspace-utils/workspace-utils";
import { getDefaultWorkspace } from "badlands-shared";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const posthog = usePostHog();
  const [user, setUser] = useState(null);
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState();
  const [abstractRequestId, setAbstractRequestId] = useState(null);
  const fetchAuthUser = useGetAuthUser(false);
  const checkedForUser = useRef(false);

  const saveUserState = useCallback((user) => {
    const workspaceId = resolveWorkspaceId({
      lastWorkspaceId: getLastWorkspaceId(),
      workspaces: user?.Workspaces,
      fallbackWorkspaceId: getDefaultWorkspace(user)?.id,
    });

    setLastWorkspaceId(workspaceId);
    setDefaultWorkspaceId(workspaceId);
    setUser(user);
    setAbstractRequestId(null);
    if (user.jwt) {
      setToken(user.jwt);
    }
    return workspaceId;
  }, []);

  useEffect(() => {
    if (fetchAuthUser.isSuccess && fetchAuthUser.data) {
      saveUserState(fetchAuthUser.data);
    }
  }, [fetchAuthUser.isSuccess, fetchAuthUser.data, saveUserState]);

  const setLoginAuth = useCallback(
    (user) => {
      posthogIdentifyUser(posthog, user);
      posthogIdentifyWorkspace(posthog, user);
      setToken(user.jwt);
      return saveUserState(user);
    },
    [posthog, saveUserState],
  );

  const logoutAuth = useCallback(
    async (redirectUser = true) => {
      try {
        clearUserState();
        removeToken();
        posthogReset(posthog);
        // cant use useNavigate here since this wraps the Router
        if (redirectUser) {
          window.location.href = LOGIN_ROUTE;
        }
      } catch (e) {
        console.error(e);
      }
    },
    [posthog],
  );

  const checkUserAuthSession = useCallback(
    (force) => {
      const getUser = async () => {
        try {
          const token = getStoredToken();
          const { exp, sharingId } = token ? decodeJWT(token) : { exp: 0 };
          if (checkSession(exp, false)) {
            setToken(token);
            if (token && !sharingId) {
              if (window.location.pathname !== SHARING_ABSTRACT_ROUTE) {
                await fetchAuthUser.refetch();
              }
            } else if (sharingId) {
              setAbstractRequestId(sharingId);
            }
          }
        } catch {
          logoutAuth(false);
        }
      };

      if (force || (getStoredToken() && !user && !checkedForUser.current)) {
        checkedForUser.current = true;
        getUser();
      }

      if (!user && fetchAuthUser.isSuccess) {
        const fetchedUser = fetchAuthUser.data;
        if (fetchedUser.id) {
          posthogIdentifyUser(posthog, fetchedUser);
          posthogIdentifyWorkspace(posthog, fetchedUser);
          saveUserState(fetchedUser);
        }
      }
    },
    [fetchAuthUser, posthog, saveUserState, user, logoutAuth],
  );
  const isOauthUser = user?.provider !== PROVIDERS.NONE.name;

  checkUserAuthSession();
  return (
    <AuthContext
      value={useMemo(
        () => ({
          user,
          invalidate: fetchAuthUser.invalidate,
          refetch: fetchAuthUser.refetch,
          defaultWorkspaceId,
          token: getStoredToken(),
          isOauthUser,
          abstractRequestId,
          setLoginAuth,
          logoutAuth,
          setUser,
          checkUserAuthSession,
        }),
        [
          user,
          defaultWorkspaceId,
          isOauthUser,
          abstractRequestId,
          setLoginAuth,
          logoutAuth,
          checkUserAuthSession,
          fetchAuthUser.invalidate,
          fetchAuthUser.refetch,
        ],
      )}
    >
      {children}
    </AuthContext>
  );
};
export default AuthProvider;
export const useAuth = () => {
  return useContext(AuthContext);
};
